import React from 'react';

// Hooks
import { useTranslation } from 'Shared/utils/hooks/use-translation';
import { useToggle } from 'Shared/utils/hooks';

import WpButton, { Color } from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';

// Styles
import './ConfirmModal.scss';

const DEFAULT_CONFIRM_BUTTON_COLOR = 'danger';

type Props = {
  isOpen: boolean;
  onToggle: Function;
  title?: string | React.ReactNode;
  message?: string;
  children?: React.ReactNode;
  cancelButtonLabel?: string;
  confirmButtonLabel?: string;
  confirmButtonColor?: Color;
  isProcessing?: boolean;
  isConfirmDisabled?: boolean;
  onConfirmClick: (event: React.MouseEvent) => void;
  withConfirmationCheckbox?: boolean;
  confirmationCheckboxLabel?: string;
};

function ConfirmModal({
  isOpen,
  onToggle,
  title,
  message,
  children,
  isProcessing = false,
  isConfirmDisabled = false,
  cancelButtonLabel,
  confirmButtonColor = DEFAULT_CONFIRM_BUTTON_COLOR,
  confirmButtonLabel,
  onConfirmClick,
  withConfirmationCheckbox = false,
  confirmationCheckboxLabel,
}: Props) {
  const { tr } = useTranslation();
  const [isChecked, toggleIsChecked] = useToggle(false);
  const titleToShow = title ?? tr('perform.confirm_modal.title');
  const messageToShow = message ?? tr('perform.confirm_modal.message');
  const cancelLabelToShow = cancelButtonLabel ?? tr('perform.common.cancel');
  const confirmLabelToShow = confirmButtonLabel ?? tr('perform.confirm_modal.confirm_button');
  const body = children || messageToShow;

  const baseClassName = 'ConfirmModal';
  return (
    <WpModal
      isOpen={isOpen}
      size="sm"
      onToggle={() => onToggle()}
      dismissOnBackdrop={false}
      innerClassName={baseClassName}
    >
      <WpModalHeader>{titleToShow}</WpModalHeader>
      <WpModalBody>
        {body}
        {withConfirmationCheckbox && confirmationCheckboxLabel && (
          <div className={`${baseClassName}__checkbox-container`}>
            <WpCheckbox
              id="confirmation-checkbox"
              label={confirmationCheckboxLabel}
              checked={isChecked}
              onChange={toggleIsChecked}
            />
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={() => onToggle()} disabled={isProcessing}>
            {cancelLabelToShow}
          </WpButton>
          <WpButton
            qaTest="confirm-button"
            onClick={onConfirmClick}
            color={confirmButtonColor}
            disabled={isConfirmDisabled || (withConfirmationCheckbox && !isChecked)}
            loading={isProcessing}
          >
            {confirmLabelToShow}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default ConfirmModal;
